import { mirrorX, strokeWidth } from "apparatus/draw";
import { Gradients } from "apparatus/gradients";
import { P, Pivot, R, Segments } from "apparatus/library/common";
import { Group, Path, Shape } from "paper";
const GRAY_CYLINDER = Gradients.cylinder("#848489".color());
export class DeskLamp {
    constructor() {
        this.drivers = [
            {
                type: "rectangle",
                minX: -50, maxX: 300,
                minY: -340, maxY: -100,
                fromAppearance: a => P(a.headX, -a.headY).multiply(a.scale),
                toAppearance: (f, p, a) => f({ headX: p.x / a.scale, headY: -p.y / a.scale }),
                scale: 1.0,
            }
        ];
    }
    render(appearance) {
        const armLength = 170;
        const armWidth = 20;
        // Solve 2-arm IK, from 0 to (headX, -headY)
        //         +
        //         |       XXX(headX, -headY)
        //         |     XXXXX
        //         |  XXXX  X
        //         |XX
        //      XXX|
        //  XXXX   |
        //  XX     |
        //    X    |
        //    X    |
        //     X   |
        //      X  |
        //      XX |
        //       XX+
        const headVector = P(appearance.headX, -appearance.headY);
        const length = Math.min(headVector.length, armLength * 1.99);
        headVector.length = length;
        const halfX = length / 2;
        const armAngle = Math.acos(halfX / armLength).toDegrees();
        const arm1 = Shape.Rectangle(R(-armWidth / 2, 0, armLength + armWidth, armWidth, Pivot.CENTER_LEFT), armWidth / 2);
        arm1.rotate(-armAngle, P(0, 0));
        arm1.fillColor = "#aaaaaa";
        arm1.withStroke(strokeWidth("default"), "#888888");
        const arm2 = arm1.clone();
        arm2.scale(-1.0, 1.0, P(halfX, 0));
        const arms = new Group([arm1, arm2]);
        arms.rotate(headVector.angle, P(0, 0));
        const lampAttachment = Shape.Rectangle(R(0, -8, 16, 40, Pivot.TOP_CENTER), 5.0);
        lampAttachment.withStroke(strokeWidth("default"), "#61605f").withFill("#888888");
        const lampHead = new Path(Segments([[0, 20], , [50, 0]], [[50, 100]]));
        mirrorX(lampHead);
        lampHead.closePath();
        lampHead.withStroke(strokeWidth("default"), "#61605f").withGradientFill("right", GRAY_CYLINDER);
        const lightColor = this.glowColor(appearance.lightColor);
        const lightCone = new Path(Segments([[50, 100]], [[80, 200]]));
        mirrorX(lightCone);
        lightCone.closePath();
        lightCone.withGradientFill("down", [[lightColor + "80", 0.0], [lightColor + "00", 1.0]]);
        const lampTop = new Group([
            lampAttachment, lightCone, lampHead
        ]);
        lampTop.pivot = P(0, 0);
        lampTop.position = headVector;
        lampTop.rotation = -appearance.angle;
        const base = Shape.Rectangle(R(0, 0, 120, 25, Pivot.TOP_CENTER), 2.0);
        base.withStroke(strokeWidth("default"), "#61605f").withGradientFill("right", GRAY_CYLINDER);
        const graphic = new Group([arms, base, lampTop]);
        // Apply scale, around the pivot.
        graphic.scale(appearance.scale, P(0, 0));
        return {
            graphic: graphic,
            hitShape: graphic.bounds.toShape(),
            pivot: P(0, 0),
            snapping: base.placeable(),
        };
    }
    glowColor(color) {
        switch (color) {
            case "yellow": return "#fce653";
            case "red": return "#ff957a";
            case "blue": return "#5192f5";
            case "green": return "#67e22d";
            case "uv": return "#9200e2";
        }
    }
}
DeskLamp.properties = {
    label: "Desk lamp",
    flippable: true,
    defaultAppearance: {
        headX: 10,
        headY: 300,
        angle: 30,
        scale: 1.0,
        lightColor: "yellow"
    },
    appearanceSpecs: [
        {
            spec: "slider", key: "angle", label: "Head angle",
            min: 0, max: 180, pipsStep: 90,
        },
        {
            spec: "slider", key: "scale", label: "Scale",
            min: 0.6, max: 1.2, step: 0.01, unit: "%",
        },
        {
            spec: "color-wells", key: "lightColor", label: "Colour",
            colors: [
                { value: "yellow", label: "Yellow" },
                { value: "red", label: "Red" },
                { value: "green", label: "Green" },
                { value: "blue", label: "Blue" },
                { value: "uv", label: "UV", hex: "#9200e2" },
            ]
        },
    ],
};
